import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import styles from "./WorkOffersInternal.module.scss";
import InternalWorkOfferCard, {
  InternalWorkOffer,
} from "../../components/MercadoDeTrabajo/InternalWorkOfferCard.tsx";
import WorkApplyModal from "../WorkBag/WorkApplyModal";
import { getAllWorkOffers } from "../../store/actions";
import BotoneraModern from "../../components/Botonera/BotoneraModern.tsx";
import Breadcrumbs from "../../components/UI/Breadcrumbs/Breadcrumbs.tsx";
import useNavVisibility from "../../hooks/useNavVisibility.ts";
import WorkOffersFilters, {
  WorkOffersFilterState,
} from "../../components/MercadoDeTrabajo/WorkOffersFilters.tsx";
import WorkOfferDetailsModal from "../../components/MercadoDeTrabajo/WorkOfferDetailsModal.tsx";

const WorkOffersInternal = () => {
  const dispatch = useDispatch<any>();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const navigate = useNavigate();

  const offersState = useSelector((state: any) => state.register?.allJobRequests);
  const offers: InternalWorkOffer[] = useMemo(
    () => offersState?.workOffers ?? [],
    [offersState]
  );
  const userData = useSelector((state: any) => state.general?.userData);
  const userEmail: string = userData?.email ?? userData?.username ?? "";
  const userIdentityValidated: boolean = Boolean(
    userData?.jobBoard?.identity_validated ?? userData?.identity_validated ?? false
  );

  const [filters, setFilters] = useState<WorkOffersFilterState>({
    search: "",
    skills: [],
    zones: [],
  });
  const [selectedOffer, setSelectedOffer] = useState<InternalWorkOffer | null>(null);
  const [detailOffer, setDetailOffer] = useState<InternalWorkOffer | null>(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getAllWorkOffers());
  }, [dispatch]);

  useEffect(() => {
    if (offersState !== undefined) {
      setIsLoading(false);
    }
  }, [offersState]);

  const skillOptions = useMemo(() => {
    const set = new Map<string, number>();
    offers.forEach((offer) => {
      (offer.skill ?? []).forEach((item) => {
        const name = item?.skill?.name;
        if (!name) return;
        set.set(name, (set.get(name) ?? 0) + 1);
      });
    });
    return Array.from(set.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [offers]);

  const zoneOptions = useMemo(() => {
    const set = new Map<string, number>();
    offers.forEach((offer) => {
      (offer.workZones ?? []).forEach((zone) => {
        const label = zone?.jurisdiccion?.name ?? zone?.city;
        if (!label) return;
        set.set(label, (set.get(label) ?? 0) + 1);
      });
    });
    return Array.from(set.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label]) => label);
  }, [offers]);

  const filteredOffers = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return offers
      .filter((offer) => {
        if (term) {
          const haystack = [
            offer.description,
            offer.user?.razon_social,
            offer.user?.name,
            offer.user?.last_name,
            ...(offer.skill ?? []).map((item) => item?.skill?.name ?? ""),
            ...(offer.workZones ?? []).map((zone) => zone?.jurisdiccion?.name ?? zone?.city ?? ""),
          ]
            .join(" ")
            .toLowerCase();
          if (!haystack.includes(term)) {
            return false;
          }
        }

        if (filters.skills.length > 0) {
          const offerSkills = new Set(
            (offer.skill ?? [])
              .map((item) => item?.skill?.name)
              .filter(Boolean) as string[]
          );
          if (!filters.skills.every((skill) => offerSkills.has(skill))) {
            return false;
          }
        }

        if (filters.zones.length > 0) {
          const offerZones = new Set(
            (offer.workZones ?? [])
              .map((zone) => zone?.jurisdiccion?.name ?? zone?.city)
              .filter(Boolean) as string[]
          );
          if (!filters.zones.some((zone) => offerZones.has(zone))) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        const dateA = new Date(a.createdAt ?? 0).getTime();
        const dateB = new Date(b.createdAt ?? 0).getTime();
        return dateB - dateA;
      });
  }, [offers, filters]);

  const handleApply = (offer: InternalWorkOffer) => {
    if (userIdentityValidated) {
      navigate(`/bolsa-trabajo/${offer.id}`);
      return;
    }

    setSelectedOffer(offer);
    setShowApplyModal(true);
  };

  const handleViewDetail = (offer: InternalWorkOffer) => {
    setDetailOffer(offer);
  };

  return (
    <>
      <div className={styles.page}>
        {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
        <div className={styles.wrapper}>
          <div className={styles.breadcrumbs}>
            <Breadcrumbs />
          </div>
          <header className={styles.header}>
            <div className={styles.titleRow}>
              <div className={styles.titleGroup}>
                <h1 className={styles.title}>Bolsa de Trabajo</h1>
                <p className={styles.subtitle}>
                  Explorá oportunidades publicadas por empresas y profesionales de la comunidad Edify.
                </p>
              </div>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => navigate("/mercado-de-trabajo/crearoferta")}
              >
                <PlusCircle size={18} />
                Crear nueva oferta
              </button>
            </div>
          </header>

          <section className={styles.content}>
            <aside className={styles.filters}>
              <WorkOffersFilters
                skills={skillOptions}
                zones={zoneOptions}
                value={filters}
                onChange={setFilters}
                totalCount={filteredOffers.length}
              />
            </aside>

            <section className={styles.offers}>
              <div className={styles.summaryRow}>
                <div className={styles.summary}>
                  <span className={styles.summaryHighlight}>{filteredOffers.length}</span>
                  <span className={styles.summaryText}>
                    {filteredOffers.length === 1 ? "oferta disponible" : "ofertas disponibles"}
                  </span>
                </div>
              </div>

              {isLoading ? (
                <div className={styles.loadingState}>
                  <div className={styles.loader} />
                  <p>Cargando ofertas…</p>
                </div>
              ) : filteredOffers.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyTitle}>No encontramos ofertas con esos criterios</div>
                  <p className={styles.emptyDescription}>
                    Probá ajustando los filtros o explorá todas las oportunidades disponibles.
                  </p>
                </div>
              ) : (
                <div className={styles.list}>
                  {filteredOffers.map((offer) => (
                    <InternalWorkOfferCard
                      key={offer.id}
                      offer={offer}
                      onApply={handleApply}
                      onViewDetail={() => handleViewDetail(offer)}
                    />
                  ))}
                </div>
              )}
            </section>
          </section>
        </div>
      </div>

      {showApplyModal && selectedOffer && (
        <WorkApplyModal
          workOffer={selectedOffer as any}
          setShowApplyModal={setShowApplyModal}
          forbiddenClose={false}
          setShowForm={() => {}}
          skipWorkBagUserCheck
          prefilledEmail={userEmail || undefined}
          identityValidated={userIdentityValidated}
        />
      )}
      <WorkOfferDetailsModal
        offer={detailOffer}
        isOpen={Boolean(detailOffer)}
        onClose={() => setDetailOffer(null)}
        onApply={(offer) => {
          setDetailOffer(null);
          handleApply(offer);
        }}
      />
    </>
  );
};

export default WorkOffersInternal;

